import * as React from 'react';
import { styled } from '@glitz/react';
import Link from 'webapp/components/Link';
import { ArrowRight } from 'webapp/components/icons';
import Image from 'webapp/components/image';
import { createMaxSmallViewportDecorator } from 'webapp/viewport';
import { TestIdProps } from '@avensia/test-automation';
import { addNavigationEventToDataLayer, type NavigationEvent } from 'webapp/tracking/events/main-navigation';
import { useFlyout } from 'webapp/shell/flyout-context';
import { isSvenssons } from 'webapp/theme';

type ItemPropType = {
  pushLevel?: (item: NordicNest.Api.MenuItemApiModel) => void;
  item: NordicNest.Api.MenuItemApiModel;
  linkText?: string;
  navigationData?: NavigationEvent;
};

type PropType = ItemPropType;

function isParent(item: NordicNest.Api.MenuItemApiModel) {
  return Boolean(item.childPages && item.childPages.length > 0);
}

export default styled(function Item(props: PropType) {
  const { close } = useFlyout('Menu');
  const showArrow = isParent(props.item) && !props.linkText;

  function onClick(e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement | HTMLDivElement>) {
    if (showArrow) {
      e.preventDefault();
      props.pushLevel?.(props.item);
    } else {
      props.navigationData && addNavigationEventToDataLayer({ ...props.item, ...props.navigationData });
      close();
    }
  }

  const Base = props?.item?.link?.to ? Link : showArrow ? Button : ItemEntry;
  const linktext = props?.item?.link?.to ? (props.linkText ?? props.item.link.children) : props.item.link.children;

  return (
    <Base css={itemDecorator} to={props.item.link.to} onClick={onClick} {...TestIdProps.SideBarProductCategory}>
      {props.item.icon ? (
        <IconLink>
          <Icon image={props.item.icon} />
          {linktext}
        </IconLink>
      ) : (
        linktext
      )}
      {showArrow && <ArrowForward />}
    </Base>
  );
});

const itemDecorator = styled(
  createMaxSmallViewportDecorator({
    fontWeight: t => t.mobileMenu?.fontWeight,
    fontSize: t => t.mobileMenu?.fontSize,
    padding: { x: t => t.sharedSpacing.l, y: t => t.sharedSpacing.m },
  }),
  {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    whiteSpace: 'nowrap',
    padding: { x: t => t.spacing.l, y: t => t.spacing.s },
  },
  isSvenssons && {
    height: 72,
  },
);

const Button = styled.button({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  color: 'inherit',
  padding: { xy: 0 },
  border: { xy: { width: 0 } },
  backgroundColor: 'transparent',
  width: '100%',
});

const ItemEntry = styled.div({
  width: '100%',
});

const IconLink = styled.span({
  display: 'flex',
  alignItems: 'center',
  gap: t => (isSvenssons ? t.sharedSpacing.l : t.sharedSpacing.s),
});

const Icon = styled(
  Image,
  {
    height: 22,
    width: 22,
    objectFit: 'contain',
  },
  isSvenssons && {
    fontSize: t => t.fontSize.m,
    display: 'block',
    height: '1.5em',
    width: '1.5em',
  },
);

const ArrowForward = styled(
  ArrowRight,
  !isSvenssons && {
    fontSize: t => t.fontSize.xxs,
  },
);
